import React, { useEffect, useState } from "react";

import Table from "../../../core/pagination/datatable";
import { useSelector, useDispatch } from "react-redux";
import CommonFooter from "../../../core/common/footer/commonFooter";
import { canSee } from "../../../core/utils/permissions";
import {
  fetchBankAccounts,
  removeBankAccount,
  openModal,
  closeModal,
  openDeleteModal,
  closeDeleteModal,
} from "../../../core/redux/bankAccount.slice";
import { fetchCustomers } from "../../../core/redux/customer.slice";
import { fetchSuppliers } from "../../../core/redux/supplier.slice";
import { Link } from "react-router-dom";
import AccountListModal from "./accountListModal";
import SettingsSideBar from "../../../feature-module/settings/settingssidebar";
import StickyBar from "../../../core/common/StickyBar";
import { Button } from "antd";
import { useTranslation } from "react-i18next";

const Accountlist = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [accountToDelete, setAccountToDelete] = useState(null);
  const [isReadMode, setIsReadMode] = useState(false);

  const { bankAccounts, loading, showModal, showDeleteModal } = useSelector(
    (state) => state.bankAccounts,
  );
  const { customers } = useSelector((state) => state.customer);
  const { suppliers } = useSelector((state) => state.supplier);
  const { user } = useSelector((state) => state.auth);
  const canDelete =
    canSee(user?.roles, "ADMIN") || canSee(user?.roles, "SUPERADMIN");

  useEffect(() => {
    dispatch(fetchBankAccounts());
    dispatch(fetchCustomers());
    dispatch(fetchSuppliers());
  }, [dispatch]);

  const getAccountHolderName = (account) => {
    if (account.companyId) return "Les Galets";
    if (account.customerId) {
      const customer = customers.find(
        (c) => c.customerId === account.customerId,
      );
      return customer?.customerName || `Client ID: ${account.customerId}`;
    }
    if (account.supplierId) {
      const supplier = suppliers.find(
        (s) => s.supplierId === account.supplierId,
      );
      return supplier?.supplierName || `Fournisseur ID: ${account.supplierId}`;
    }
    return "N/A";
  };

  const getAccountType = (account) => {
    if (account.companyId) return "Société";
    if (account.customerId) return "Client";
    if (account.supplierId) return "Fournisseur";
    return "N/A";
  };

  const handleAdd = () => {
    setIsEditMode(false);
    setSelectedAccount(null);
    dispatch(openModal());
  };

  const handleView = (account) => {
    setIsReadMode(true);
    setIsEditMode(true);
    setSelectedAccount(account);
    dispatch(openModal());
  };

  const handleEdit = (account) => {
    setIsEditMode(true);
    setIsReadMode(false);
    setSelectedAccount(account);
    dispatch(openModal());
  };

  const handleDelete = (account) => {
    setAccountToDelete(account);
    dispatch(openDeleteModal());
  };

  const handleCancel = () => {
    setIsReadMode(false);
    dispatch(closeModal());
  };

  const deleteAccount = () => {
    dispatch(removeBankAccount(accountToDelete.accountId));
  };

  const columns = [
    {
      title: t("accountList.columns.accountHolder"),
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: "bold" }}>
            {getAccountHolderName(record)}
          </div>
          <div style={{ fontSize: "12px", color: "#666" }}>
            {getAccountType(record)}
          </div>
        </div>
      ),
      sorter: (a, b) =>
        getAccountHolderName(a).localeCompare(getAccountHolderName(b)),
    },
    {
      title: t("accountList.columns.rib"),
      dataIndex: "rib",
      sorter: (a, b) => (a.rib || "").localeCompare(b.rib || ""),
    },
    {
      title: t("accountList.columns.iban"),
      dataIndex: "iban",
      sorter: (a, b) => (a.iban || "").localeCompare(b.iban || ""),
    },
    {
      title: t("accountList.columns.bank"),
      dataIndex: "bankName",
      sorter: (a, b) => (a.bankName || "").localeCompare(b.bankName || ""),
    },
    {
      title: t("accountList.columns.agency"),
      dataIndex: "bankAgency",
      sorter: (a, b) => (a.bankAgency || "").localeCompare(b.bankAgency || ""),
    },
    {
      title: t("accountList.columns.bic"),
      dataIndex: "bic",
      sorter: (a, b) => (a.bic || "").localeCompare(b.bic || ""),
    },
    {
      title: "",
      dataIndex: "actions",
      key: "actions",
      render: (_, record) => (
        <div className="action-table-data">
          <div className="edit-delete-action">
            <Link
              className="me-2 p-2"
              to="#"
              onClick={() => handleView(record)}
            >
              <i data-feather="eye" className="feather-eye"></i>
            </Link>
            <Link
              className="me-2 p-2"
              to="#"
              onClick={() => handleEdit(record)}
            >
              <i data-feather="edit" className="feather-edit"></i>
            </Link>
            {canDelete && (
              <Link
                className="confirm-text p-2"
                to="#"
                onClick={() => handleDelete(record)}
              >
                <i data-feather="trash-2" className="feather-trash-2"></i>
              </Link>
            )}
          </div>
        </div>
      ),
    },
  ];

  return (
    <div>
      <div className="page-wrapper">
        <div className="content">
          <div className="tab-content" id="pills-tabContent">
            <div
              className="tab-pane fade show active"
              id="pills-home"
              role="tabpanel"
              aria-labelledby="pills-home-tab"
            >
              <StickyBar
                title={t("accountList.title")}
                count={bankAccounts.length}
                actions={
                  <Button className="btn-customized-primary">
                    <Link to="#" onClick={handleAdd}>
                      <i className="ti ti-circle-plus me-1"></i>
                      {t("accountList.addAccount")}
                    </Link>
                  </Button>
                }
              />

              <div className="row">
                <div className="col-xl-12">
                  <div className="settings-wrapper d-flex">
                    <SettingsSideBar />
                  <div className="flex-fill w-50">
                      {showModal ? (
                        <AccountListModal
                          onCancel={handleCancel}
                          isEdit={isEditMode}
                          initialData={selectedAccount}
                          loading={loading}
                          isReadMode={isReadMode}
                        />
                      ) : (
                        <div className="card table-list-card">
                          <div className="card-header d-flex align-items-center justify-content-between">
                            <h4> {t("accountList.bankAccounts")}</h4>
                          </div>
                          <div className="card-body">
                            <div className="table-responsive">
                              <Table
                                columns={columns}
                                dataSource={bankAccounts}
                                loading={loading}
                                rowKey="accountId"
                              />
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div
                    className="tab-pane fade"
                    id="pills-profile"
                    role="tabpanel"
                    aria-labelledby="pills-profile-tab"
                  ></div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <CommonFooter />
      </div>
      {/* <AccountListModal
        open={showModal}
        onCancel={handleCancel}
        isEdit={isEditMode}
        initialData={selectedAccount}
        loading={loading}
        isReadMode={isReadMode}
      /> */}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div
          className="modal fade show"
          style={{ display: "block" }}
          id="delete-modal"
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="page-wrapper-new p-0">
                <div className="content p-5 px-3 text-center">
                  <span className="rounded-circle d-inline-flex p-2 bg-danger-transparent mb-2">
                    <i className="ti ti-trash fs-24 text-danger" />
                  </span>
                  <h4 className="fs-20 fw-bold mb-2 mt-1">
                    {t("accountList.deleteModal.title")}
                  </h4>
                  <p className="mb-0 fs-16">
                    {t("accountList.deleteModal.message")}
                  </p>
                  <div className="modal-footer-btn mt-3 d-flex justify-content-center">
                    <button
                      type="button"
                      className="btn me-2 btn-secondary fs-13 fw-medium p-2 px-3 shadow-none"
                      onClick={() => dispatch(closeDeleteModal())}
                    >
                      {t("common.cancel")}
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary fs-13 fw-medium p-2 px-3"
                      onClick={() => {
                        deleteAccount();
                        dispatch(closeDeleteModal());
                      }}
                    >
                      {t("common.confirmDelete")}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Accountlist;
